/**
 * Le chat: une seule conversation, partagee par tout le monde.
 *
 * POURQUOI DU TEXTE ET PAS DE LA VOIX. La voix demanderait WebRTC, une
 * permission de micro, un encodage, et une gestion d'echo — un projet entier, et
 * un projet qui touche a la vie privee de gens qui jouent depuis leur salon. Le
 * texte tient sur le transport qui existe deja (`client/reseau/salon.ts`), coute
 * quelques octets, se relit dans un journal, et se lit dans un test.
 *
 * POURQUOI EN DOM. Comme les noms qui flottent au-dessus des tetes: du texte
 * dessine dans la scene voudrait dire une texture ou une geometrie de lettres,
 * donc des appels de dessin en plus, alors que le budget est deja compte. Une
 * poignee de `<div>` ne coute rien a la carte graphique et s'ecrit avec les
 * vrais accents.
 *
 * CE QU'ON AFFICHE N'EST JAMAIS CE QU'ON A TAPE. Rien n'apparait a l'envoi: on
 * attend que le serveur renvoie la ligne, avec le nom qu'IL connait. Ce que le
 * joueur voit est donc exactement ce que les autres voient.
 */
import '../styles/ui.css'
import type { MessageChat } from '../../shared/types/reseau.ts'
import { HISTORIQUE_CHAT, MAX_TEXTE_CHAT, nettoyerTexteChat } from '../../shared/sim/reseau.ts'
import { couleurCompte } from '../../shared/data/proprietaires.ts'
import { journal } from '../log/journal.ts'
import { clicSonore, el, hex } from './commun.ts'
import { depiler, empiler, type Couche } from './retour.ts'

/**
 * Duree pendant laquelle une ligne reste lisible quand le chat est ferme.
 *
 * Assez longtemps pour lire une phrase qu'on n'attendait pas, assez court pour
 * qu'un bavardage ne mange pas le coin de l'ecran pendant toute la partie.
 * Ouvrir le chat les fait toutes revenir: rien n'est perdu, seulement range.
 */
const DUREE_VISIBLE_MS = 12_000

export class Chat {
  /**
   * Envoie la ligne. Rend faux s'il n'y a pas de liaison — un invite, ou le
   * wifi qui repart — pour qu'on puisse le dire au joueur.
   */
  onEnvoyer: ((texte: string) => boolean) | null = null

  private readonly racine: HTMLDivElement
  private readonly lignes: HTMLDivElement
  private readonly formulaire: HTMLFormElement
  private readonly champ: HTMLInputElement
  private readonly boutonOuvrir: HTMLButtonElement
  private ouvert = false
  /**
   * Numero de la derniere ligne affichee. Apres une coupure, le serveur renvoie
   * l'historique en entier: sans ce garde, toute la conversation se repeterait a
   * chaque retour du reseau.
   */
  private dernierNumero = 0
  private minuteries: number[] = []

  private readonly surClavier = (e: KeyboardEvent): void => {
    // « Echap » depuis le champ est traite par le champ lui-meme, qui arrete la
    // propagation: ici on ne voit donc que les touches destinees au jeu.
    if (e.key !== 'Enter' || e.ctrlKey || e.altKey || e.metaKey) return
    if (this.ouvert) {
      // Deja ouvert, mais le joueur a touche le monde entre-temps et le champ a
      // perdu le curseur: « Entree » l'y ramene, au lieu de ne rien faire du
      // tout — ce qui donne l'impression que le chat s'est bloque.
      e.preventDefault()
      this.champ.focus({ preventScroll: true })
      return
    }
    // « Entree » ouvre le chat, sauf si le joueur ecrit deja ailleurs (le nom de
    // son bonhomme, un mot de passe) ou si un panneau modal est devant: la
    // touche appartient alors a ce qui est au premier plan.
    if (this.saisieAilleurs() || this.dialogueOuvert()) return
    e.preventDefault()
    this.ouvrir('touche Entrée')
  }

  private readonly couche: Couche = { nom: 'chat', fermer: () => this.fermer('touche retour') }

  /** Hauteur du clavier virtuel a la derniere mesure, en pixels CSS. */
  private clavier = 0

  /**
   * LE CHAMP AU-DESSUS DU CLAVIER.
   *
   * Sur Android, le clavier ne retrecit pas la page (`innerHeight` reste le
   * meme): il se pose PAR-DESSUS. Mesure sur le Pixel 9 le 12 octobre: champ a
   * 497-541 px, partie visible de 385 px — on ecrivait a l'aveugle. La partie
   * visible, elle, est connue (`visualViewport`): on remonte le chat de la
   * hauteur du clavier.
   *
   * Et quand le clavier se range (touche « retour ») sur un champ VIDE, on
   * ferme: il n'y a plus rien a ecrire, et le champ reste sinon pose sur les
   * boutons du bas.
   */
  private readonly surVueVisible = (): void => {
    const vv = window.visualViewport
    if (!vv) return
    const avant = this.clavier
    this.clavier = Math.max(0, Math.round(window.innerHeight - (vv.height + vv.offsetTop)))
    this.racine.style.setProperty('--clavier', `${this.clavier}px`)
    if (this.ouvert && avant > 120 && this.clavier < 40 && this.champ.value.trim() === '') {
      this.fermer('clavier range')
    }
  }

  constructor(parent: HTMLElement) {
    this.racine = el('div', 'chat')
    this.lignes = el('div', 'chat__lignes')

    this.formulaire = el('form', 'chat__saisie')
    this.formulaire.hidden = true
    this.champ = el('input', 'chat__champ')
    this.champ.id = 'chat-champ'
    this.champ.type = 'text'
    this.champ.autocomplete = 'off'
    this.champ.maxLength = MAX_TEXTE_CHAT
    this.champ.placeholder = 'Écris à tout le monde…'
    this.champ.setAttribute('aria-label', 'Message pour tout le monde')
    const envoi = el('button', 'chat__envoyer', 'Envoyer')
    envoi.type = 'submit'
    // UNE CROIX POUR FERMER. Au telephone il n'y a ni « Echap » ni « Entree »:
    // la seule sortie etait d'envoyer un message vide, ce que personne ne
    // devine — et le champ restait pose sur les boutons du bas.
    const croix = el('button', 'chat__fermer', '✕')
    croix.type = 'button'
    croix.setAttribute('aria-label', 'Fermer le chat')
    croix.addEventListener('click', () => {
      clicSonore()
      this.fermer('croix')
    })
    this.formulaire.append(this.champ, envoi, croix)
    this.formulaire.addEventListener('submit', (e) => {
      e.preventDefault()
      this.envoyer()
    })
    this.champ.addEventListener('keydown', (e) => {
      // BARRIERE. Les raccourcis du jeu (v, l, a, r) ecoutent la FENETRE, sans
      // regarder ou va la frappe: sans cette ligne, ecrire « avale » montait
      // dans une voiture, jetait l'ancre et allumait la lampe pendant qu'on
      // tapait. Ce qu'on ecrit dans le champ reste dans le champ.
      e.stopPropagation()
      if (e.key === 'Escape') {
        e.preventDefault()
        this.fermer('Echap')
      }
    })

    // Le bouton existe pour le telephone, qui n'a pas de touche « Entree » tant
    // qu'aucun champ n'a le focus: sans lui, le chat y serait inatteignable.
    this.boutonOuvrir = el('button', 'chat__ouvrir')
    this.boutonOuvrir.type = 'button'
    this.boutonOuvrir.title = 'Parler à tout le monde (Entrée)'
    this.boutonOuvrir.setAttribute('aria-label', 'Parler à tout le monde')
    this.boutonOuvrir.append(el('span', 'chat__ouvrir-icone', '💬'))
    this.boutonOuvrir.addEventListener('click', () => {
      clicSonore()
      this.ouvrir('bouton')
    })

    this.racine.append(this.lignes, this.formulaire, this.boutonOuvrir)
    parent.append(this.racine)
    window.addEventListener('keydown', this.surClavier)
    window.visualViewport?.addEventListener('resize', this.surVueVisible)
    journal.info('chat', 'chat en place')
  }

  // ------------------------------------------------------------- reception

  /** Affiche les lignes recues du serveur, en ignorant celles deja vues. */
  recevoir(messages: readonly MessageChat[]): void {
    for (const m of messages) {
      if (m.n <= this.dernierNumero) continue
      this.dernierNumero = m.n
      journal.info('chat', `${m.nom}: ${m.texte}`, { compte: m.compte })
      this.ajouter(this.ligne(m.nom, couleurCompte(m.compte), m.texte))
    }
  }

  private ligne(nom: string, couleur: number, texte: string): HTMLDivElement {
    const ligne = el('div', 'chat__ligne')
    const auteur = el('span', 'chat__nom', nom)
    // La couleur du COMPTE, pas une couleur tiree du nom: c'est la meme que
    // celle de ses maisons et de son socle, on reconnait qui parle sans lire.
    auteur.style.color = hex(couleur)
    ligne.append(auteur, el('span', 'chat__texte', texte))
    return ligne
  }

  /** Ligne du jeu lui-meme (pas de compte, donc pas de couleur de joueur). */
  private ajouterSysteme(texte: string): void {
    const ligne = el('div', 'chat__ligne chat__ligne--systeme')
    ligne.append(el('span', 'chat__texte', texte))
    this.ajouter(ligne)
  }

  private ajouter(ligne: HTMLDivElement): void {
    this.lignes.append(ligne)
    while (this.lignes.childElementCount > HISTORIQUE_CHAT) this.lignes.firstElementChild?.remove()
    // Toujours montrer la derniere: on lit une conversation par le bas.
    this.lignes.scrollTop = this.lignes.scrollHeight
    this.plusTard(() => ligne.classList.add('chat__ligne--vieille'), DUREE_VISIBLE_MS)
  }

  // -------------------------------------------------------------- ouverture

  private ouvrir(par: string): void {
    if (this.ouvert) return
    this.ouvert = true
    this.racine.classList.add('chat--ouvert')
    this.formulaire.hidden = false
    this.boutonOuvrir.hidden = true
    // Focus IMMEDIAT, pas differe: le joueur vient d'appuyer sur Entree pour
    // ecrire, la premiere lettre qu'il tape doit atterrir dans le champ.
    this.champ.focus({ preventScroll: true })
    this.lignes.scrollTop = this.lignes.scrollHeight
    empiler(this.couche)
    journal.input('chat', `ouverture (${par})`)
  }

  /** Vrai si le chat est ouvert (la touche « retour » le ferme en premier). */
  get estOuvert(): boolean {
    return this.ouvert
  }

  fermer(par: string): void {
    if (!this.ouvert) return
    // Fermee par « retour », l'entree d'historique est deja partie.
    if (par !== 'touche retour') depiler(this.couche)
    this.ouvert = false
    this.racine.classList.remove('chat--ouvert')
    this.formulaire.hidden = true
    this.boutonOuvrir.hidden = false
    // Rendre le clavier au jeu: sans ce `blur`, le champ garde le focus et les
    // touches de raccourci (v, l, r) partent dans la saisie au lieu du monde.
    this.champ.blur()
    journal.input('chat', `fermeture (${par})`)
  }

  private envoyer(): void {
    const texte = nettoyerTexteChat(this.champ.value)
    this.champ.value = ''
    if (texte === null) {
      // Rien a dire: on referme sans rien envoyer. Appuyer deux fois sur Entree
      // est la facon la plus naturelle de renoncer.
      this.fermer('envoi vide')
      return
    }
    journal.input('chat', `envoi: ${texte}`)
    if (this.onEnvoyer?.(texte) !== true) {
      this.ajouterSysteme('Personne ne t’entend : pas de liaison avec le monde partagé.')
      journal.warn('chat', 'ligne non envoyee: pas de liaison')
    }
    this.fermer('envoi')
  }

  // ----------------------------------------------------------------- divers

  /** Vrai si le joueur ecrit deja quelque part: la touche ne nous revient pas. */
  private saisieAilleurs(): boolean {
    const actif = document.activeElement
    if (!(actif instanceof HTMLElement) || actif === document.body) return false
    const balise = actif.tagName
    return balise === 'INPUT' || balise === 'TEXTAREA' || balise === 'SELECT' || actif.isContentEditable
  }

  /** Vrai si un panneau modal, le createur ou l'ecran de connexion est devant. */
  private dialogueOuvert(): boolean {
    return document.querySelector('.panneau-fond--ouvert, .creation, .connexion') !== null
  }

  private plusTard(f: () => void, ms: number): void {
    const id = window.setTimeout(() => {
      this.minuteries = this.minuteries.filter((x) => x !== id)
      f()
    }, ms)
    this.minuteries.push(id)
  }

  detruire(): void {
    for (const m of this.minuteries) window.clearTimeout(m)
    this.minuteries = []
    window.removeEventListener('keydown', this.surClavier)
    window.visualViewport?.removeEventListener('resize', this.surVueVisible)
    this.onEnvoyer = null
    this.racine.remove()
  }
}
